<script>
import LoadingSpinner from "@/javascript/views/components/loadingSpinner.vue";
import {useFundStore} from "../../../stores/fund";
import {useAnalyticEventStore} from "@/javascript/stores/analyticEvents";
import Tooltip from "../../components/tooltip.vue";
import DragNDrop from "../brand/dragNDrop.vue";

export default {
  components: {DragNDrop, Tooltip, LoadingSpinner},
  props: {

  },

  setup() {
    const fundStore = useFundStore();
    const analyticEventStore = useAnalyticEventStore();
    return {
      fundStore,
      analyticEventStore,
    };
  },
  data() {
    return {
      errors: {},
      saving: false,
      saved: false,
      newFund: {
        name: "",
        short_description: "",
        description: "",
        donor_wall_enabled: true,
        visible: true,
        donation_suggestions_enabled: true,
        donation_suggestions: [25, 50, 100, 250]
      },
      addingSuggestion: false,
      tempSuggestion: "",
    };
  },
  watch: {
  },
  methods: {
    validate() {
      this.errors = {};
      if (!this.newFund.name) {
        this.errors['name'] = "Fund name required";
      }

      return Object.entries(this.errors).length === 0;
    },
    async save() {
      if (this.validate()) {
        this.saving = true;
        try {
          await this.fundStore.createFund(this.newFund);
          this.saved = true;
          this.analyticEventStore.logEvent({
            action: `new-fund-modal-save`,
            metadata: {
              fund: this.newFund
            }
          });
        } catch (e) {
          this.saving = false;
          this.errors['name'] = e.message || 'Failed to create fund';
        }
      }
    },
    handleFileSelected(file) {
      this.newFund.banner_image = file;
    },
    addSuggestion() {
      const amount = parseFloat(this.tempSuggestion);
      if (!isNaN(amount) && amount > 0 && this.newFund.donation_suggestions.length < 4 && !this.newFund.donation_suggestions.includes(amount)) {
        this.newFund.donation_suggestions.push(amount);
      }
      this.tempSuggestion = "";
      this.addingSuggestion = false;
    },
    closeModal() {
      this.fundStore.showNewModal = false;
      this.analyticEventStore.logEvent({
        action: `new-fund-modal-close`,
      });
    }
  }
};
</script>

<template>
  <div
    class="fixed inset-0 flex items-center justify-center z-50"
  >
    <div class="fixed inset-0 bg-black opacity-50" />
    <div
      class="relative bg-white p-6 max-w-screen-md mx-auto rounded-lg shadow-lg transition-all w-full max-h-[90vh] flex flex-col"
      :class="saving || saved? 'h-1/4' : ''"
    >
      <div
        v-if="saved"
        class="m-20 text-blue-500 text-large text-center"
      >
        Fund Created!
      </div>
      <div
        v-else-if="saving"
        class="m-20"
      >
        <loading-spinner size="small" />
      </div>
      <div
        v-else
        class="flex flex-col flex-1 min-h-0"
      >
        <!-- Static Header -->
        <div class="flex flex-row justify-between items-center mb-4 flex-shrink-0">
          <div>
            <p class="text-lg font-semibold">
              Create a new fund
            </p>
          </div>
          <button
            class="text-gray-500 hover:text-gray-700 focus:outline-none"
            @click="closeModal"
          >
            <svg
              class="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
        <div class="border-t border-gray-300 mb-2 flex-shrink-0" />

        <!-- Scrollable Body -->
        <div class="flex-1 overflow-y-auto">
          <div class="font-semibold" />
          <div
            class="transition-all"
          >
            <div>
              <div>
                <div>
                  <label
                    for="fund_name"
                    class=" text-xs font-semibold mt-2"
                  >Fund Name</label>

                  <input
                    id="donor_l_name"
                    v-model="newFund.name"
                    placeholder="Enter Name"
                    class="mt-1 w-full px-3 py-2 border border-gray-300 shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                  >
                  <!--   New Short Description Column -->
                  <label
                    for="fund_short_description"
                    class=" text-xs font-semibold mt-2"
                  >Short Description </label>
                  <tooltip
                    class="mt-3"
                    message="A brief description of your giving need."
                    color="#3a4473"
                  />
                  <textarea
                    id="donor_l_name"
                    v-model="newFund.short_description"
                    placeholder="Enter a short description"
                    class="mt-1 w-full px-3 py-2 border border-gray-300 shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                  />
                  <p
                    class="text-right text-xs text-gray-500"
                  >
                    {{ newFund.short_description.length }}/100
                  </p>

                  <!--  Kept the original description column - only call it Long Description for labels-->
                  <label
                    for="fund_description"
                    class=" text-xs font-semibold mt-2"
                  >Long Description</label>
                  <tooltip
                    class="mt-3 ml-2"
                    message="A longer description for donors looking for more information on your current needs."
                    color="#3a4473"
                  />
                  <textarea
                    id="donor_l_name"
                    v-model="newFund.description"
                    rows="4"
                    placeholder="Enter description"
                    class="mt-1 w-full px-3 py-2 border border-gray-300 shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                  />
                  <span class="text-xs text-red-700">
                    {{ errors['name'] }}
                  </span>
                  <div>
                    <label
                      for="banner"
                      class="text-xs font-semibold mt-2"
                    >
                      Banner Image
                    </label>
                    <drag-n-drop
                      id="banner"
                      compact
                      validate
                      file-type="image"
                      @file-selected="handleFileSelected"
                    />
                  </div>

                  <!-- Donation Suggestions -->
                  <div class="flex items-center gap-2 mt-4">
                    <input
                      id="donation_suggestions_enabled"
                      v-model="newFund.donation_suggestions_enabled"
                      type="checkbox"
                      class="w-4 h-4 rounded border-gray-300 text-default_primary_dark focus:ring-default_primary_dark cursor-pointer"
                    >
                    <label for="donation_suggestions_enabled" class="text-xs font-semibold cursor-pointer">
                      Donation Suggestions
                    </label>
                    <tooltip
                      message="Show suggested donation amounts to donors on the giving form."
                      color="#3a4473"
                    />
                  </div>
                  <div v-if="newFund.donation_suggestions_enabled" class="mt-2">
                    <div class="flex flex-wrap items-center gap-2">
                      <div
                        v-for="(amount, idx) in newFund.donation_suggestions"
                        :key="idx"
                        class="flex items-center gap-1 bg-gray-100 border border-gray-300 rounded-full px-3 h-8"
                      >
                        <span class="text-sm font-semibold">${{ amount }}</span>
                        <button
                          class="text-gray-400 hover:text-red-500 ml-1 font-bold leading-none"
                          @click="newFund.donation_suggestions.splice(idx, 1)"
                        >×</button>
                      </div>
                      <div v-if="addingSuggestion" class="flex items-center gap-1">
                        <input
                          v-model="tempSuggestion"
                          type="number"
                          class="w-20 border border-gray-300 rounded px-2 py-1 text-sm h-8"
                          placeholder="$"
                          @keyup.enter="addSuggestion"
                        >
                        <button
                          class="bg-default_secondary_dark text-white rounded-full w-8 h-8 flex items-center justify-center text-sm"
                          @click="addSuggestion"
                        >✓</button>
                      </div>
                      <button
                        v-else-if="newFund.donation_suggestions.length < 4"
                        class="bg-default_secondary_dark text-white rounded-full w-8 h-8 flex items-center justify-center text-sm"
                        @click="addingSuggestion = true"
                      >+</button>
                    </div>
                    <p class="text-xs text-gray-500 mt-1">Maximum of 4 suggestions.</p>
                  </div>

                  <!-- Donor Wall Toggle -->
                  <div class="flex items-center gap-2 mt-4">
                    <input
                      id="donor_wall_enabled"
                      v-model="newFund.donor_wall_enabled"
                      type="checkbox"
                      class="w-4 h-4 rounded border-gray-300 text-default_primary_dark focus:ring-default_primary_dark cursor-pointer"
                    >
                    <label for="donor_wall_enabled" class="text-xs font-semibold cursor-pointer">
                      Enable Donor Wall
                    </label>
                    <tooltip
                      message="When enabled, donors can choose to have their name displayed publicly on the donor wall for this fund."
                      color="#3a4473"
                    />
                  </div>

                  <!-- Visible Toggle -->
                  <div class="flex items-center gap-2 mt-4">
                    <input
                      id="visible"
                      v-model="newFund.visible"
                      type="checkbox"
                      class="w-4 h-4 rounded border-gray-300 text-default_primary_dark focus:ring-default_primary_dark cursor-pointer"
                    >
                    <label for="visible" class="text-xs font-semibold cursor-pointer">
                      Visible
                    </label>
                    <tooltip
                      message="When disabled, this fund will be hidden from your donation page. You can make it visible later."
                      color="#3a4473"
                    />
                  </div>

                </div>
              </div>
            </div>
          </div>
          <div class="w-full flex flex-col pt-4 mt-4 border-t border-gray-200">
            <button
              class="button-filled px-4 py-2 rounded block"
              @click="save"
            >
              Create Fund
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">

</style>
